/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect, PropsWithChildren } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import Head from "next/head";
import {
  Box,
  LinkButton,
  ResponsiveLayout,
  Snackbar,
  Typography,
  Banner,
  OverlayProvider,
} from "@bca/ui-library";
import { useSelector, useDispatch, shallowEqual } from "react-redux";

import getStringFrom from "../helpers/getStringFrom";
import { useStateRef } from "../hooks/customHooks";
import pushPageView from "../analytics/pushPageView";
import getFilteredImagesByType from "../helpers/getFilteredImagesByType";

import { VehicleDetailsProps } from "../vehicleDetails/vehicle-details-props.type";
import Layout from "../components/Layout";
import WarrantedMileage from "../components/WarrantedMileage";
import GuidePrice from "../components/GuidePrice";
import ConditionReport from "../components/ConditionReport";
import MechanicalReport from "../components/MechanicalReport";
import VehicleSpecification from "../components/VehicleSpecification";
import ConditionAndMechanicalReports from "../components/ConditionAndMechanicalReports";
import PricingGuide from "../components/PricingGuide";
import ImageGallery from "../components/ImageGallery";
import BuyingOptions from "../components/buyingOptions/BuyingOptions";
import VehicleInteraction from "../components/VehicleInteraction";
import SaleInformation from "../components/SaleInformation";
import VehicleNotFound from "../components/VehicleNotFound";
import { addDuplicates } from "../helpers/addDuplicates";
import ClickableImage from "../components/ClickableImage";
import ImageZoomModal from "../components/ImageZoomModal";
import Owners from "../components/Owners";
import HPISpecCheck from "../components/HPISpecCheck";
import HPISpecCheckTable from "../components/HPISpecCheckTable";
import HPIErrorBanner from "../components/hpi/HPIErrorBanner";
import pushToDigitalData from "../analytics/pushToDigitalData";
import { Image } from "../vehicleDetails/vehicle-details-dto.type";
import BackToResultsStrip from "../components/backToResults/BackToResultsStrip";
import useURLQueryParams from "../hooks/useURLQueryParams";
import { getAnalyticsPageVariant } from "../analytics/AnalyticsUtils";
import LotNavigationBanner from "../components/lotNavigationBanner/LotNavigationBanner";
import { useRouter } from "next/dist/client/router";

import VersionNavigationBanner from "../components/versionNavigationBanner/VersionNavigationBanner";
import useSourcePage from "../hooks/useSourcePage";
import { useViewport } from "../hooks/useViewport";
import {
  GET_APP_STATE,
  SET_IS_LOADING_FILE,
  SET_IS_PRINT_SERVICE_UNAVAILABLE,
  SET_IS_VENDOR,
} from "../redux/slices/appSlice";
import { GET_SOURCE_STATE, SET_SOURCE } from "../redux/slices/sourceSlice";
import { StoreType } from "../redux/StoreType";
import { MechanicalItemReportAnalytics } from "../analytics/MechanicalItemReportAnalytics";
import Impersonation from "../components/impersonation/Impersonation";
import {
  GET_USER_STATE,
  SET_USER_VENDOR_ADMIN_ROLE,
} from "../redux/slices/userSlice";
import useVehiclesDetailsPageData from "../hooks/useVehiclesDetailsPageData.hook";
import ValuationModal from "../components/BCAValuation/ValuationModal";
import ValuationPrice from "../components/BCAValuation/ValuationPrice";
import useValuationEndpoint from "../hooks/useValuationEndpoint.hook";
import BatteryReport from "../components/BatteryReport";
import BatteryGradeSlideout from "../components/BatteryGradeSlideout";
import pushBatteryGradeData from "../analytics/pushBatteryGradeData";
import { FeatureFlagProvider } from "bca-feature-flag-provider";
import { FEATURE_FLAGS } from "../components/constants/featureFlags";
import SalvageDeclarations from "../components/salvageDeclarations/SalvageDeclarations";
import { GET_VENDOR_IMPERSONATION_STATE } from "../redux/slices/impersonationSlice";

export const GreySectionRow = styled.div<
  PropsWithChildren<JSX.IntrinsicElements["div"]>
>`
  background-color: #fafafa;
  min-height: 280px;
  overflow: auto;
`;

export const UsabillaContainer = styled.div`
  background-color: #fafafa;
  padding-bottom: 40px;
  ${css({
    paddingX: ["20px", "20px", "20px", "auto"],
  })};
`;

export const StyledResponsiveLayout = styled(ResponsiveLayout)`
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  padding-top: 20px;
  padding-bottom: 20px;
  ${css({
    justifyContent: ["initial", "initial", "initial", "space-between"],
  })}
`;

export const pushImageZoomToAnalytics = (
  images: Image[],
  activeImageIndex: number,
  isVendorAdmin: boolean,
  isVendorImpersonationSet: boolean
) => {
  images.splice(0, 2).splice(images.length - 2, 2); // removes first 2 and last 2 items
  const wantedImage = images[activeImageIndex - 1];
  const wantedImageType = wantedImage?.imageType.toLowerCase();

  const filteredImages = getFilteredImagesByType(
    images,
    wantedImageType === "damage" ? "damage" : "beauty"
  );
  const indexOfWantedImagePerCategory = filteredImages.indexOf(wantedImage) + 1;
  if ((isVendorAdmin && isVendorImpersonationSet) || isVendorAdmin === false) {
    pushToDigitalData({
      event: {
        name: "click",
        category: "button",
        label: `Image|${activeImageIndex}`,
        action: "open-gallery",
        section: "in-page",
      },
      product: {
        image: {
          imageType:
            wantedImage?.imageType.toLowerCase() === "damage"
              ? "damage"
              : "beauty",
          imageNum: indexOfWantedImagePerCategory,
          imagePlacementNum: activeImageIndex,
        },
      },
    });
  }
};

const VehicleDetailsIDP: React.FunctionComponent<VehicleDetailsProps> = (
  props
) => {
  const { vdr } = useURLQueryParams();

  const router = useRouter();
  const { source } = router.query;
  const dispatch = useDispatch();

  const {
    app: { isLoadingFile, isPrintServiceUnavailable },
  }: StoreType = useSelector(GET_APP_STATE, shallowEqual);
  const {
    source: { urlSource },
  }: StoreType = useSelector(GET_SOURCE_STATE, shallowEqual);
  const {
    user: { isVendorAdmin },
  }: StoreType = useSelector(GET_USER_STATE, shallowEqual);
  const {
    impersonation: { isVendorImpersonationSet },
  }: StoreType = useSelector(GET_VENDOR_IMPERSONATION_STATE, shallowEqual);

  const viewportWidth = useViewport(100);
  const [showValuationModal, setShowValuationModal] = useState(false);
  const { shouldRestrict } = useVehiclesDetailsPageData(vdr);
  const { bcaValuationResult, isBCAValuationLoading, isBCAValuationAvailable } =
    useValuationEndpoint(props.vrm, props.isProvisional);

  useEffect(() => {
    dispatch(SET_SOURCE(source));
  }, [source]);

  const isPostSaleVDP = urlSource === "Purchases" || urlSource === "Invoices";

  useEffect(() => {
    dispatch(SET_USER_VENDOR_ADMIN_ROLE(props.isVendorAdmin));
  }, [props.isVendorAdmin, dispatch]);

  useEffect(() => {
    dispatch(SET_IS_VENDOR(props.isVendor));
  }, [props.isVendorOfVehicle]);

  const isOnSaleAndOwner = props.isVendorOfVehicle && props.isAllocatedToSale;
  const isOnSaleButNotOwner =
    !props.isVendorOfVehicle && props.isAllocatedToSale;

  if (props.fetchError) {
    return (
      <Layout
        user={
          props.username
            ? { firstName: props.username, lastName: props.lastName || "" }
            : undefined
        }
        idToken={props.idToken}
        isToUseAuthZero={props.isToUseAuthZero}
        isAuthenticated={false}
      >
        <VehicleNotFound />
      </Layout>
    );
  }

  const {
    analyticsProps,
    username,
    lastName,
    isAdmin,
    isFeatureVehicle,
    description,
    mileage,
    vin,
    mileageWarranted,
    grade,
    mechanicalReport,
    conditionReport,
    batteryReport,
    vehicleType,
    vrm,
    color,
    manufacturerColour,
    fuel,
    transmission,
    doors,
    logBook,
    registrationDate,
    vatType,
    serviceHistory,
    lastServiceDate,
    MOTExpiryDate,
    owners,
    prices,
    assessment,
    saleDetails: {
      name,
      publishedName,
      startDate,
      endDate,
      sectionName,
      location,
      saleCode,
      saleTypeCode,
      saleTypeName,
      lolCode,
      status,
      numberOfLots,
    },
    images,
    lotNumber,
    isTracked: trackState,
    isFeatured,
    lotNote,
    biddingType,
    biddingInfoUrl,
    placeBidUrl,
    acceptTermsAndConditionsUrl,
    printSections,
    userType,
    hpi,
    additionalInformation,
    declaration,
    FromSearch,
    resolvedUrl,
    degradedServices,
    significantFields,
    locationUrl,
    navigation,
    serviceHistoryData,
    insuranceTotalLoss,
    equipmentItems,
    isProvisional,
    isLatestVersion,
    hasPreviousVersion,
    soldDate,
    inventoryData: { inventoryID },
    customerNumber,
    appConfigurationUrl,
  } = props;

  const { isVPSoldSourcePage, isVPExceptSoldPageOrTransportTab } =
    useSourcePage();

  const { CAPClean } = prices;

  const shouldShowPricing = () => {
    if (isVPExceptSoldPageOrTransportTab) {
      return true;
    }

    const isAnyPriceAvailable = Object.values(prices).some((price) => {
      return Boolean(price.value);
    });
    return isAnyPriceAvailable;
  };

  const imagesWithDuplicates =
    images.length < 2 ? images : addDuplicates(images);

  const SLIDE_SPACING = 10;
  const [showImageZoomModal, setShowImageZoomModal] = useState(false);
  const [isTracked, setIsTracked] = useState(trackState);
  const [isFeaturedVehicle, setIsFeaturedVehicle] = useState(isFeatured);
  const [hpiError, setHpiError] = useState(false);

  const mechanicalReportAnalyticsData = MechanicalItemReportAnalytics({
    reportType: mechanicalReport.type,
    categoryMapping: assessment?.categoryMapping,
  });

  const isSalvageVehicle = declaration?.salvagedVehicle ?? false;

  useEffect(() => {
    if (!props.isAdobeLaunchLoaded) return;

    mechanicalReportAnalyticsData?.length > 0 &&
      ((isVendorAdmin && isVendorImpersonationSet) ||
        isVendorAdmin === false) &&
      pushPageView({
        peep_analyticsid: analyticsProps.aaId,
        ssoId: analyticsProps.ssoId,
        casId: analyticsProps.cas,
        userType: analyticsProps.userType,
        isVendorAdmin: isVendorAdmin,
        vrm: vrm,
        saleCode: saleCode,
        lotNumber: lotNumber,
        saleType: saleTypeName,
        location: location,
        saleStartDate: startDate,
        saleEndDate: endDate,
        vehicleId: analyticsProps.vehicleId,
        hasHpi: Boolean(hpi),
        images: images,
        mechanicalReportAnalyticsData: mechanicalReportAnalyticsData,
        pageVariant: getAnalyticsPageVariant(vdr),
        vehicleType: vehicleType,
        evBattery: additionalInformation?.evBattery,
        isSalvageVehicle: isSalvageVehicle,
      });
  }, [
    props.isAdobeLaunchLoaded,
    analyticsProps.aaId,
    analyticsProps.ssoId,
    analyticsProps.cas,
    analyticsProps.userType,
    isVendorAdmin,
    isVendorImpersonationSet,
    vrm,
    saleCode,
    lotNumber,
    saleTypeCode,
    location,
    startDate,
    endDate,
    analyticsProps.vehicleId,
    hpi,
    images,
    mechanicalReportAnalyticsData,
    vdr,
    vehicleType,
    additionalInformation?.evBattery,
    isSalvageVehicle,
    saleTypeName,
  ]);

  useEffect(() => {
    if (
      degradedServices?.length > 0 &&
      degradedServices.includes("InventoryService")
    ) {
      setHpiError(true);
    }
  }, [degradedServices, setHpiError]);

  useEffect(() => {
    if (isPrintServiceUnavailable) {
      const timer = setTimeout(() => {
        dispatch(SET_IS_PRINT_SERVICE_UNAVAILABLE(false));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [dispatch, isPrintServiceUnavailable]);

  const [activeImageIndex, setActiveImageIndex, activeImageIndexRef] =
    useStateRef(images.length === 1 ? 0 : 2);

  const handleModalImage = (images: Image[], index: number) => {
    setActiveImageIndex(index);
    setShowImageZoomModal(true);
    pushImageZoomToAnalytics(
      images,
      index - 1,
      isVendorAdmin,
      isVendorImpersonationSet
    );
  };

  const handleCloseSnackBar = () => {
    dispatch(SET_IS_LOADING_FILE(false));
  };

  const [isSlideoutOpen, setIsSlideoutOpen] = useState<boolean>(false);

  const analyticsData = {
    saleCode,
    vrm,
    lotNumber,
    saleTypeCode,
    location,
    startDate,
    endDate,
    vehicleId: analyticsProps.vehicleId,
    hpi: Boolean(hpi),
    images,
    mechanicalReportAnalyticsData,
    vehicleType,
    evBattery: additionalInformation?.evBattery,
  };

  const handleOpenBatteryGradeSlideout = () => {
    setIsSlideoutOpen(true);
    pushBatteryGradeData(
      {
        action: "open",
        category: "link",
        label: "ev-battery|overlay",
        name: "click",
        section: "primary-information",
        value: "",
      },
      analyticsData
    );
  };

  const handleCloseBatteryGradeSlideout = () => setIsSlideoutOpen(false);

  const isOnSale = isOnSaleButNotOwner || isOnSaleAndOwner;

  return (
    <Layout
      user={
        username ? { firstName: username, lastName: lastName || "" } : undefined
      }
      idToken={props.idToken}
      isToUseAuthZero={props.isToUseAuthZero}
      isAuthenticated={false}
    >
      <Head>
        <title>{description}</title>
      </Head>
      <FeatureFlagProvider
        clientSecret={appConfigurationUrl}
        keys={FEATURE_FLAGS}
        context={{ userId: customerNumber }}
      >
        {isVendorAdmin && vdr === "vp" && (
          <Impersonation userRole={props.userRole} vrm={vrm} />
        )}
        <OverlayProvider>
          <BatteryGradeSlideout
            isOpen={isSlideoutOpen}
            onClose={handleCloseBatteryGradeSlideout}
            data={{
              condition: additionalInformation?.evBattery?.batteryCondition,
              reportUrl: batteryReport.href,
            }}
            analyticsData={analyticsData}
          />
        </OverlayProvider>
        {hpiError && (
          <ResponsiveLayout
            height="0px"
            css={`
              top: ${FromSearch === "true" ? "40px" : "0px"};
            `}
          >
            <HPIErrorBanner
              closeBanner={() => setHpiError(false)}
            ></HPIErrorBanner>
          </ResponsiveLayout>
        )}
        {FromSearch === "true" && (
          <Box backgroundColor="#080f22">
            <ResponsiveLayout>
              <LinkButton
                text="Back to Results"
                variant="tertiary"
                icon="arrow-left-solid"
                iconAlignment="left"
                iconColor="white"
                px="0px"
                shouldOpenInNewWindow={false}
                analytics={{
                  eventLabel: "back-to-results",
                  eventAction: "navigate",
                }}
                css={`
                  color: white;
                  cursor: pointer;
                  text-decoration: underline;
                  font-size: 14px;
                  font-weight: normal;
                  max-width: 1080px;
                  display: flex;
                  flex-direction: row;
                  justify-content: flex-start;
                `}
                link={`${
                  process.env.auctionViewUrl
                }buyer/facetedsearch?${getStringFrom(
                  resolvedUrl,
                  "searchQuery="
                )}`}
              ></LinkButton>
            </ResponsiveLayout>
          </Box>
        )}
        {isPrintServiceUnavailable && (
          <Banner
            variant="warning"
            title="Printing and Exporting Services Unavailable"
            primaryLinkLabel="Refresh"
            secondaryLinkLabel="Dismiss"
            primaryLinkAction={router.reload}
            secondaryLinkAction={() =>
              dispatch(SET_IS_PRINT_SERVICE_UNAVAILABLE(false))
            }
            displayMode="overlay"
            paddingX={60}
            paddingY={
              viewportWidth === "mobile" || viewportWidth === "tablet"
                ? 130
                : 80
            }
          >
            <Typography margin={0} variant="body">
              We were unable to print or export your selected vehicles. Please
              refresh the page to try again.
            </Typography>
          </Banner>
        )}
        {isLoadingFile && (
          <Snackbar
            title="Your download will open shortly in a new tab."
            verticalPosition="bottom"
            actionLabel="Dismiss"
            displayTime={10000}
            action={handleCloseSnackBar}
            onClose={handleCloseSnackBar}
            paddingY="20px"
            paddingX={viewportWidth === "desktop" ? "90px" : "60px"}
          />
        )}
        <BackToResultsStrip vrm={vrm} />
        {isVPSoldSourcePage &&
          ((isLatestVersion && hasPreviousVersion) || !isLatestVersion) && (
            <VersionNavigationBanner
              isLatestVersion={isLatestVersion}
              soldDate={soldDate}
            />
          )}
        <ImageGallery
          images={images}
          activeImageIndex={activeImageIndex}
          setActiveImageIndex={setActiveImageIndex}
          activeImageIndexRef={activeImageIndexRef}
          overlaySlot={
            <VehicleInteraction
              isOnSale={isOnSale}
              vrm={vrm}
              lotNumber={lotNumber}
              saleCode={saleCode}
              description={description}
              lotNote={lotNote}
              isTracked={isTracked}
              setIsTracked={setIsTracked}
              printSections={printSections}
              isProvisional={isProvisional}
              isFeatured={isFeaturedVehicle}
              setIsFeatured={setIsFeaturedVehicle}
              isAdmin={isAdmin}
              isFeatureVehicle={isFeatureVehicle}
              shouldRestrict={shouldRestrict}
              inventoryId={inventoryID}
            />
          }
          slideSpacing={SLIDE_SPACING}
        >
          {imagesWithDuplicates.map((image, index) => (
            <ClickableImage
              overflow="hidden"
              mr={`${SLIDE_SPACING}px`}
              key={image?.imageURI + index}
              width={[340, 600, 715]}
              height={[190, 335, 400]}
              imageURI={image?.imageURI}
              onClick={() => handleModalImage(imagesWithDuplicates, index)}
            />
          ))}
        </ImageGallery>
        {showImageZoomModal && (
          <ImageZoomModal
            activeImageIndex={activeImageIndex}
            setActiveImageIndex={setActiveImageIndex}
            activeImageIndexRef={activeImageIndexRef}
            imagesWithDuplicates={imagesWithDuplicates}
            images={images}
            setShowImageZoomModal={setShowImageZoomModal}
          />
        )}
        <StyledResponsiveLayout
          display="flex"
          py={5}
          flexWrap="wrap"
          alignItems="flex-start"
        >
          <WarrantedMileage
            mileCount={mileage}
            mileageWarranted={mileageWarranted}
          />
          <Owners ownersCount={owners} />
          <ConditionReport grade={grade} anchorLink={conditionReport.href} />
          {additionalInformation?.evBattery?.isApplicable && (
            <BatteryReport
              grade={additionalInformation?.evBattery?.batteryCondition}
              onSlideoutOpen={handleOpenBatteryGradeSlideout}
            />
          )}
          <MechanicalReport
            reportType={mechanicalReport.type}
            anchorLink={mechanicalReport.href ?? ""}
            categoryMapping={assessment?.categoryMapping}
          ></MechanicalReport>
          {Boolean(hpi) && (
            <HPISpecCheck
              starRating={hpi.optionsRating}
              price={hpi.optionsPrice}
              anchorLink="#hpi-spec-check-table"
            />
          )}
          <GuidePrice
            anchorLink="#guide-pricing"
            price={CAPClean.value}
            soldDate={soldDate}
          />
          {isSalvageVehicle && <SalvageDeclarations />}
          {isVPExceptSoldPageOrTransportTab && (
            <ValuationPrice
              anchorLink="#guide-pricing"
              price={bcaValuationResult}
              isBCAValuationLoading={isBCAValuationLoading}
              isBCAValuationAvailable={isBCAValuationAvailable}
            />
          )}
        </StyledResponsiveLayout>
        {(isOnSaleAndOwner || isOnSaleButNotOwner) &&
          !isProvisional &&
          !isPostSaleVDP && (
            <GreySectionRow data-testid="sale-and-buying">
              <ResponsiveLayout
                display="flex"
                flexWrap={{
                  mobile: "wrap",
                  tablet: "wrap",
                  desktop: "nowrap",
                }}
                justifyContent="space-between"
                py={9}
              >
                <SaleInformation
                  biddingType={biddingType}
                  saleName={name}
                  publishedName={publishedName}
                  startDate={startDate}
                  endDate={endDate}
                  sectionName={sectionName}
                  localSaleLocation={location}
                  saleLocationUrl={locationUrl}
                  lolCode={lolCode}
                  status={status}
                  numberOfLots={numberOfLots}
                  vehicleLocationDescription={
                    additionalInformation?.vehicleLocationDescription
                  }
                ></SaleInformation>
                <BuyingOptions
                  biddingType={biddingType}
                  biddingInfoUrl={biddingInfoUrl}
                  placeBidUrl={placeBidUrl}
                  acceptTermsAndConditionsUrl={acceptTermsAndConditionsUrl}
                  vrm={vrm}
                  isTracked={isTracked}
                  setIsTracked={setIsTracked}
                  significantFields={significantFields}
                  saleStartDate={startDate}
                  isOnSaleAndOwner={isOnSaleAndOwner}
                  inventoryId={inventoryID}
                />
              </ResponsiveLayout>
            </GreySectionRow>
          )}
        <VehicleSpecification
          vehicleType={vehicleType}
          color={color}
          manufacturerColour={manufacturerColour}
          fuel={fuel}
          transmission={transmission}
          doors={doors}
          vin={vin}
          logBook={logBook}
          owners={owners}
          registrationDate={registrationDate}
          vatType={vatType}
          serviceHistory={serviceHistory}
          lastServiceDate={lastServiceDate}
          MOTExpiryDate={MOTExpiryDate}
          numberOfKeys={additionalInformation?.numberOfKeys}
          dvsaMileage={additionalInformation?.dvsaMileageEntries}
          notes={serviceHistoryData?.notes}
          lastServiceMileage={serviceHistoryData?.lastService?.mileage}
          serviceCount={serviceHistoryData?.serviceCount}
          declaration={declaration}
          co2Emissions={additionalInformation?.emissions?.cO2}
          noxEmissions={additionalInformation?.emissions?.nox}
          equipmentItems={equipmentItems}
          advisoryItems={additionalInformation?.advisoryItems}
          insuranceTotalLoss={insuranceTotalLoss}
        />
        <ConditionAndMechanicalReports
          vehicleType={vehicleType}
          mechanicalReport={mechanicalReport}
          conditionReport={conditionReport}
          grade={grade}
          conditionSummaries={assessment && assessment.conditionSummaries}
        />

        {Boolean(hpi) && (
          <HPISpecCheckTable
            starRating={hpi.optionsRating}
            basePrice={hpi.vehiclePriceWithVat}
            optionsPrice={hpi.optionsPrice}
            totalPrice={hpi.totalPrice}
            specCheckOptions={hpi.specCheckOptions}
          />
        )}
        {userType !== "Viewer" && shouldShowPricing() && (
          <PricingGuide
            prices={prices}
            soldDate={soldDate}
            bcaValuation={bcaValuationResult}
            setShowValuationModal={setShowValuationModal}
            isBCAValuationLoading={isBCAValuationLoading}
            isBCAValuationAvailable={isBCAValuationAvailable}
          />
        )}
        {showValuationModal && (
          <ValuationModal
            viewportWidth={viewportWidth}
            setShowValuationModal={setShowValuationModal}
          />
        )}
        <UsabillaContainer>
          <div
            style={{
              width: "100%",
              maxWidth: 1080,
              margin: "0 auto",
              paddingTop: "25px",
            }}
            ub-in-page="5fa290dc94811e626a7fcceb"
          />
        </UsabillaContainer>
        <LotNavigationBanner
          navigation={navigation}
          description={description}
        />
      </FeatureFlagProvider>
    </Layout>
  );
};

export default VehicleDetailsIDP;
